import React, {useState} from "react";
import {Chip, CircularProgress, Grid, IconButton, InputBase, MenuItem, Select, Tooltip, Typography} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { format, isValid, parseISO } from "date-fns";
import {disciplineEntreeLabelStatus, getStatusDeeList, tipoAvaliacoes, tipoPresenca} from "../../utils/utility";
import { AlunoEnrollContext } from "./alunoEnrollContext";
import CustomWidthTooltip from "../../components/CustomWidthTooltip";
import axios from "../../store/axiosApi";
import { getDeeStatusColor, getGradeColor, getContrastText, sharedStyles } from "./alunoDialogStyles";
import { canConsultEduAccess } from "../../features/fermiEdu/policies";
import { useEduCapabilities } from "../../features/fermiEdu/capabilities";
import { EduAccessHistoryButton } from "../../features/fermiEdu/AccessHistory";

const inputSx = { fontSize: '0.72rem', textAlign: 'center', padding: '0 2px', width: '100%' } as const;

const formatSafeDate = (value: string | null | undefined, dateFormat = 'dd/MM/yyyy') => {
    if (!value) return '';
    const parsed = parseISO(value);
    return isValid(parsed) ? format(parsed, dateFormat) : '';
};

const fieldLabelSx = {
    fontSize: '0.53rem',
    fontWeight: 700,
    lineHeight: 1,
    opacity: 0.82,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
} as const;

const fieldValueSx = {
    fontSize: '0.72rem',
    lineHeight: 1.08,
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
} as const;

const sxStyles = {
    mainGrid: {
        ...sharedStyles.containerRow,
        marginBottom: '5px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'divider',
        backgroundColor: 'grey.300',
        padding: '3px',
        borderRadius: '8px',
        justifyContent: 'center',
        m: 1
    },
    fieldGrid: {
        ...sharedStyles.containerRow,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '8px',
        padding: '2px 4px',
        margin: '2px',
        minHeight: '32px',
        minWidth: 0,
        flexDirection: 'column',
        flexWrap: 'nowrap',
        alignItems: 'center',
        justifyContent: 'center'
    },
    discGrid: sharedStyles.containerRowNoWrap,
}

interface iDiscDeeDialog  {
    registro: any,
    eilId: number
}

const AlunoTabDiscDee = ({ registro, eilId  } : iDiscDeeDialog ) => {
    const { showExcluded } = React.useContext(AlunoEnrollContext);
    const { capabilities: eduCapabilities } = useEduCapabilities();
    const [editing, setEditing] = useState(false);
    const [status, setStatus] = useState(registro.Status ?? 1);
    const [frequencia, setFrequencia] = useState<string>(String(registro.FrequenciaPercentual ?? ''));
    const [nota, setNota] = useState<string>(String(registro.Nota ?? ''));
    const [aproveitamento, setAproveitamento] = useState<string>(registro.Aproveitamento != null ? String(registro.Aproveitamento) : '');
    const [dtConclusao, setDtConclusao] = useState<string>(registro.DTConclusao ?? '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            const nFreq = frequencia !== '' ? Number(frequencia) : 0;
            const nNota = nota !== '' ? Number(nota) : 0;
            await axios.put(`/disciplineentrees/${registro.id}`, {
                ...registro,
                Status: status,
                FrequenciaPercentual: nFreq,
                Nota: nNota,
                Aproveitamento: aproveitamento || null,
                DTConclusao: dtConclusao ? `${dtConclusao.substring(0, 10)} 03:00:00` : null
            });
            registro.Status = status;
            registro.FrequenciaPercentual = nFreq;
            registro.Nota = nNota;
            registro.Aproveitamento = aproveitamento || null;
            registro.DTConclusao = dtConclusao || null;
            setEditing(false);
        } catch (e) {
            console.error('Error updating:', e);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setStatus(registro.Status ?? 1);
        setFrequencia(String(registro.FrequenciaPercentual ?? ''));
        setNota(String(registro.Nota ?? ''));
        setAproveitamento(registro.Aproveitamento != null ? String(registro.Aproveitamento) : '');
        setDtConclusao(registro.DTConclusao ?? '');
        setEditing(false);
    };

    const cGridColor = getDeeStatusColor(status);
    const cNota = status === 1 ? getGradeColor(Number(nota), 5) : getDeeStatusColor(status);
    const cFreq = status === 1 ? getGradeColor(Number(frequencia), 75) : getDeeStatusColor(status);
    const textColor = getContrastText(cGridColor);
    const textColorNota = getContrastText(cNota);
    const textColorFreq = getContrastText(cFreq);

    let sAvaliacoes = "";
    if (registro.disciplineEntreeAvaliacoes) {
        registro.disciplineEntreeAvaliacoes.forEach( (a:any) => {
            sAvaliacoes += a.Nota+';';
        });
        sAvaliacoes = sAvaliacoes.substring(0,(sAvaliacoes.length - 1))
    }

    let iPresencas = 0;
    let iFaltas = 0;
    let dhUltPresenca = '';
    if (registro.disciplineEntreeFrequency) {
        registro.disciplineEntreeFrequency.forEach( (a:any) => {
            if (a.Status === 1) {
                iPresencas += 1;
                if (a.Date > dhUltPresenca) { dhUltPresenca = a.Date }
            }
            if (a.Status === 2) iFaltas += 1;
        });
    }

    const statusLabel = getStatusDeeList().find((s: any) => s.id === status)?.descricao ?? String(status);

    // Filtro de "EXCLUIDA" deve usar o status PERSISTIDO (registro.Status),
    // nao o estado local de edicao — caso contrario o componente se desmonta
    // assim que o usuario seleciona "EXCLUIDA" no dropdown, antes de Salvar.
    // Tambem nunca esconde enquanto estiver em modo de edicao.
    const persistedStatusDesc = String(disciplineEntreeLabelStatus(registro.Status) || '').toUpperCase();
    const isExcluded = persistedStatusDesc.startsWith('EXCLU');
    if (isExcluded && !showExcluded && !editing) return null;

    const ids = (<span>
        MatriculaId: {registro.Enrollment_id}<br/>
        Matricula na Disciplina: {registro.id}<br/>
        Chamada: {registro.DisciplineOffer_id}<br/><br/>

        Inicio Disc: {formatSafeDate(registro.disciplineOffer?.InitialDate)} Final Disc: {formatSafeDate(registro.disciplineOffer?.EndDate)}<br/>
        <br/>

        <Grid container>
            <Grid item >
                Frequencia: {iPresencas} Presencas;  {iFaltas} Faltas<br/>
        {
            registro.disciplineEntreeFrequency &&
            registro.disciplineEntreeFrequency.map( (a:any, ix: number) => {
                return (
                    <Grid key={ix} item container sx={{...sxStyles.mainGrid}} >
                        <Grid item  sx={{...sharedStyles.tooltipGrid}}>
                            <Typography>{a.id}</Typography>
                        </Grid>
                        <Grid item  sx={{...sharedStyles.tooltipGrid}}>
                            <Typography>{formatSafeDate(a.Date)}</Typography>
                        </Grid>
                        <Grid item  sx={{...sharedStyles.tooltipGrid}}>
                            <Typography>{tipoPresenca(a.Status)}</Typography>
                        </Grid>
                        <Grid item  sx={{...sharedStyles.tooltipGrid}}>
                            <Typography>{a.DisciplineOfferContent_id}</Typography>
                        </Grid>
                    </Grid>
                )
            })
        }
        </Grid>
            <Grid item>
        Notas: {sAvaliacoes} <br/>
        {
            registro.disciplineEntreeAvaliacoes &&
            registro.disciplineEntreeAvaliacoes.map( (a:any, ix:number) => {
                return (
                    <Grid container key={ix} sx={{...sxStyles.mainGrid}} >
                        <Grid item  sx={{...sharedStyles.tooltipGrid}}>
                            <Typography>{a.id}</Typography>
                        </Grid>
                        <Grid item  sx={{...sharedStyles.tooltipGrid}}>
                            <Typography>{formatSafeDate(a.DTAvaliacao)}</Typography>
                        </Grid>
                        <Grid item  sx={{...sharedStyles.tooltipGrid}}>
                            <Typography>{a.Nota}</Typography>
                        </Grid>
                        <Grid item  sx={{...sharedStyles.tooltipGrid}}>
                            <Typography>{tipoAvaliacoes(a.TipoAvaliacao_id)}</Typography>
                        </Grid>
                    </Grid>
                )
            })
        }
            </Grid>
        </Grid>
    </span>);

    const formatDateForInput = (val: string | null) => {
        if (!val) return '';
        return formatSafeDate(val, 'yyyy-MM-dd');
    };

    const formatDateForDisplay = (val: string | null) => {
        if (!val) return '';
        return formatSafeDate(val);
    };

    const renderField = (label: string, value: React.ReactNode) => (
        <>
            <Typography component="span" sx={fieldLabelSx}>{label}</Typography>
            {typeof value === 'string' || typeof value === 'number' ? (
                <Typography component="span" title={String(value)} sx={fieldValueSx}>{value}</Typography>
            ) : value}
        </>
    );

    return (<React.Fragment>
                <Grid container item sx={{
                    ...sxStyles.mainGrid,
                    minWidth: '390px',
                    backgroundColor: cGridColor,
                    color: textColor,
                    borderColor: textColor === '#fff' ? 'rgba(255,255,255,0.4)' : 'divider',
                    '& .MuiTypography-root': { color: 'inherit' },
                    '& .MuiChip-root': { color: 'inherit', borderColor: 'inherit' },
                    '& .MuiSvgIcon-root': { color: 'inherit' },
                    '& .MuiInputBase-input': { color: 'inherit' },
                    '& > .MuiGrid-item': { borderColor: textColor === '#fff' ? 'rgba(255,255,255,0.5)' : undefined },
                    '& .MuiSelect-select': { py: 0, textAlign: 'center' },
                }}>

                    {/* Botao editar / salvar / cancelar */}
                    <Grid item sx={{...sxStyles.fieldGrid, borderStyle: 'none', padding: 0}} sm={1} md={1} lg={1} xl={1}>
                        {!editing ? (
                            <Tooltip title="Editar">
                                <IconButton size="small" onClick={() => setEditing(true)} sx={{ padding: '2px' }}>
                                    <EditIcon sx={{ fontSize: '0.85rem', color: 'inherit' }} />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <>
                                <Tooltip title="Salvar">
                                    <span>
                                        <IconButton size="small" onClick={handleSave} disabled={saving} sx={{ padding: '1px' }}>
                                            <SaveIcon sx={{ fontSize: '0.85rem', color: 'primary.main' }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                {saving && <CircularProgress size={14} sx={{ ml: 0.5 }} />}
                                <Tooltip title="Cancelar">
                                    <span>
                                        <IconButton size="small" onClick={handleCancel} disabled={saving} sx={{ padding: '1px' }}>
                                            <CloseIcon sx={{ fontSize: '0.85rem', color: 'error.main' }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                    </Grid>

                    {/* Linha 1: Frequencia + Nota */}
                    <Grid item sx={{...sxStyles.fieldGrid, backgroundColor: cFreq, color: textColorFreq}} sm={5} md={5} lg={5} xl={5}>
                        {renderField('Freq.', editing ? (
                                <InputBase
                                    value={frequencia}
                                    onChange={(e) => { const v = e.target.value; if (v === '' || /^[\d.]*$/.test(v)) setFrequencia(v); }}
                                    inputProps={{ inputMode: 'decimal', style: inputSx, onKeyDown: (e: any) => e.stopPropagation() }}
                                />
                            ) : frequencia === '' ? 'N/D' : `${frequencia}%`
                        )}
                    </Grid>
                    <Grid item sx={{...sxStyles.fieldGrid, backgroundColor: cNota, color: textColorNota}} sm={5} md={5} lg={5} xl={5}>
                        {renderField('Nota', editing ? (
                                <InputBase
                                    value={nota}
                                    onChange={(e) => { const v = e.target.value; if (v === '' || /^[\d.]*$/.test(v)) setNota(v); }}
                                    inputProps={{ inputMode: 'decimal', style: inputSx, onKeyDown: (e: any) => e.stopPropagation() }}
                                />
                            ) : nota
                        )}
                    </Grid>

                    {/* Linha 2: Status + Aproveitamento */}
                    <CustomWidthTooltip title={editing ? '' : ids} disableHoverListener={editing} aria-label="add">
                        <Grid item sx={{...sxStyles.fieldGrid}} sm={7} md={7} lg={7} xl={7}>
                            {renderField('Status', editing ? (
                                    <Select
                                        value={status}
                                        onChange={(e) => setStatus(Number(e.target.value))}
                                        variant="standard"
                                        size="small"
                                        sx={{ fontSize: '0.72rem', width: '100%' }}
                                    >
                                        {getStatusDeeList().map((s: any) => (
                                            <MenuItem key={s.id} value={s.id}>{s.descricao}</MenuItem>
                                        ))}
                                    </Select>
                                ) : (
                                    <Chip
                                        label={statusLabel}
                                        size="small"
                                        variant="outlined"
                                        title={statusLabel}
                                        sx={{
                                            fontSize: '0.66rem',
                                            height: 18,
                                            maxWidth: '100%',
                                            '& .MuiChip-label': {
                                                overflow: 'hidden',
                                                px: 1,
                                                textOverflow: 'ellipsis',
                                            }
                                        }}
                                    />
                                )
                            )}
                        </Grid>
                    </CustomWidthTooltip>
                    <Grid item sx={{...sxStyles.fieldGrid}} sm={4} md={4} lg={4} xl={4}>
                        {renderField('Aprov.', editing ? (
                                <InputBase
                                    value={aproveitamento}
                                    onChange={(e) => setAproveitamento(e.target.value)}
                                    inputProps={{ style: inputSx, onKeyDown: (e: any) => e.stopPropagation() }}
                                />
                            ) : aproveitamento
                        )}
                    </Grid>

                    {/* Linha 3: Classe */}
                    <Grid item sx={{...sxStyles.fieldGrid}} sm={9} md={9} lg={9} xl={9}>
                        {renderField('Turma', `${registro.disciplineOffer?.classOffer?.Description ?? ''} ${registro.disciplineOffer?.classOffer?.Room ?? ''} ${registro.disciplineOffer?.classOffer?.Shift ?? ''}`.trim())}
                    </Grid>
                    <Grid item sx={{...sxStyles.fieldGrid}} sm={2} md={2} lg={2} xl={2}>
                        {renderField('DOF', registro.DisciplineOffer_id)}
                    </Grid>

                    {/* Data inicio */}
                    <Grid item sx={{...sxStyles.fieldGrid}} sm={3} md={3} lg={3} xl={3}>
                        {renderField('Inicio', formatSafeDate(registro.disciplineOffer?.InitialDate))}
                    </Grid>

                    {/* Ultima presenca */}
                    <Grid item sx={{...sxStyles.fieldGrid}} sm={3} md={3} lg={3} xl={3}>
                        {renderField('Ult. pres.', formatSafeDate(dhUltPresenca))}
                    </Grid>

                    {/* DT Conclusao */}
                    <Grid item sx={{...sxStyles.fieldGrid}} sm={3} md={3} lg={3} xl={3}>
                        {renderField('Conclusao', editing ? (
                                <InputBase
                                    type="date"
                                    value={formatDateForInput(dtConclusao)}
                                    onChange={(e) => setDtConclusao(e.target.value ? e.target.value : '')}
                                    inputProps={{ style: { fontSize: '0.72rem', padding: '0 2px', minWidth: 0, width: '100%' } }}
                                />
                            ) : formatDateForDisplay(dtConclusao)
                        )}
                    </Grid>

                    {canConsultEduAccess(eduCapabilities) && <Grid item xs={12}><EduAccessHistoryButton disciplineEntreeId={Number(registro.id)} /></Grid>}
                </Grid>
    </React.Fragment>);
}
export default (AlunoTabDiscDee);
